/**
 * WT1 - where the phone was when the walk-through started, against where the record says the building is.
 *
 * ⛔ It REPORTS, it never refuses (his decision, 2026-09-20). The stored geocode has been measured 1.02 mi
 *    off a real building, so blocking a tour on a disagreement would stop him touring the right one. The line
 *    is the cheapest guard against the hazard that has bitten this repo twice: a counter filed on the
 *    Victorville building aimed at a Hesperia one, and a read that blended two buildings silently.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Mean Earth radius in statute miles (IUGG 6,371.0088 km). */
const EARTH_RADIUS_MILES = 3958.7613;

/** Below this the two points are the same site: a lot, a GPS fix indoors, a geocode on the parcel centroid. */
export const DISAGREEMENT_MILES = 0.3;

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle (haversine) distance in miles. */
export const distanceMiles = (a: LatLng, b: LatLng): number => {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  // min(1, ...) because rounding can push h a hair past 1 for antipodal points, and asin(>1) is NaN.
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** The line the tour and the report show - or null when the two agree (or the distance is unknown). */
export const describeDistance = (miles: number | null | undefined): string | null => {
  if (typeof miles !== 'number' || !Number.isFinite(miles) || miles <= DISAGREEMENT_MILES) return null;
  return `captured ${miles.toFixed(1)} miles from this property's recorded location`;
};

const isCoord = (lat: unknown, lng: unknown): boolean =>
  typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
  && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

/**
 * The tour's start against the record's location, both nullable (location refused, a record never geocoded).
 * ⛔ A missing side is "unknown", never "agrees": it returns null and the screen says nothing, rather than a
 *    distance from (0, 0) - the Gulf of Guinea - to the building.
 */
export const describeStartDistance = (
  start: { lat: number | null | undefined; lng: number | null | undefined },
  recorded: { lat: number | null | undefined; lng: number | null | undefined },
): string | null => {
  if (!isCoord(start.lat, start.lng) || !isCoord(recorded.lat, recorded.lng)) return null;
  return describeDistance(
    distanceMiles({ lat: start.lat as number, lng: start.lng as number }, { lat: recorded.lat as number, lng: recorded.lng as number }),
  );
};

/**
 * Where the phone is, asked ONCE at the start of a walk-through (his decision 2026-09-20). ⛔ It never blocks the
 * tour: a refusal, a failed fix, no geolocation at all, or a fix that has not come back within `timeoutMs` all
 * answer null, and the tour starts without a location. A 0,0 is a broken fix, not a place - also null.
 */
export const currentLocation = (
  geolocation: Pick<Geolocation, 'getCurrentPosition'> | undefined = typeof navigator !== 'undefined'
    ? navigator.geolocation
    : undefined,
  timeoutMs = 8000,
): Promise<LatLng | null> =>
  new Promise((resolve) => {
    if (!geolocation) {
      resolve(null);
      return;
    }
    let done = false;
    const finish = (value: LatLng | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      geolocation.getCurrentPosition(
        ({ coords }) => {
          const { latitude: lat, longitude: lng } = coords;
          finish(isCoord(lat, lng) && !(lat === 0 && lng === 0) ? { lat, lng } : null);
        },
        () => finish(null),
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
      );
    } catch {
      finish(null);
    }
  });
